<template>
    <div v-show="isLoaded" class="tab-pane active schedulers">
        <div v-if="showSchedulersForm" class="profiles-with-navigation no-print">
            <div class="common-nav-wrap schedulers-sidebar">
                <Simplebar class="with-border-track">
                    <div class="common-nav">
                        <SideBar
                            :parent-name="ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS"
                            :list="schedulersForSideBar"
                            :current-id="currentSchedulerId"
                            :search-placeholder="$t('wanderManagement.schedulersTab.searchPlaceholder')"
                            :add-new-text="$t('wanderManagement.schedulersTab.addNewWirelessAccessGroup')"
                            :active-icon="'icon-available'"
                            :is-active-icon-shown="isEnabled"
                            :show-icons="showIcons"
                            @delete="deleteById"
                        />
                    </div>
                </Simplebar>
            </div>
            <div v-if="isLoaded" class="component-with-nav-body">
                <SchedulersForm
                    v-model="currentScheduler"
                    :doors="doorOptions"
                    :forbidden-names="forbiddenNames"
                    :enable-check-is-disabled="restrictedEnableCurrentScheduler"
                />
            </div>
            <Transition name="slide-fade">
                <div class="alert-wrapper">
                    <div v-if="restrictedEnableCurrentScheduler" class="full-window-info icon-allert">
                        {{
                            $t(
                                'wanderManagement.schedulersTab.notPossibleToEnableSingleLocationSchedulerInfo'
                            )
                        }}
                    </div>
                </div>
            </Transition>
        </div>
        <Transition name="slide-fade">
            <div v-if="accessModules.length === 0 && isLoaded" class="full-window-info icon-allert no-print">
                {{ $t('wanderManagement.schedulersTab.noAccessModulesInfo') }}
            </div>
        </Transition>
        <div v-if="!showSchedulersForm" class="data-empty">
            <h3>{{ $t('wanderManagement.schedulersTab.noSchedulers') }}</h3>
        </div>
        <div class="print-only">
            <h4 v-if="showPrintKeyword">{{ $t('print.search', { val: searchKeyword }) }}</h4>
            <table v-if="schedulers.length > 0" class="table schedulers">
                <thead>
                    <span class="table-row">
                        <th v-text="$t('wanderManagement.schedulersTab.scheduler.name')"></th>
                        <th v-text="$t('wanderManagement.schedulersTab.enabled')"></th>
                        <th v-text="$t('wanderManagement.schedulersTab.door')"></th>
                        <th v-text="$t('wanderManagement.schedulersTab.doorStatus')"></th>
                        <th v-text="$t('wanderManagement.schedulersTab.doorStatusOutside')"></th>
                        <th v-text="$t('wanderManagement.schedulersTab.weekSchedule')"></th>
                    </span>
                </thead>
                <tbody>
                    <tr v-for="(scheduler, index) in filteredSchedulersCopyWithDoor" :key="index">
                        <span class="table-row">
                            <td v-text="scheduler.name"></td>
                            <td><p v-if="scheduler.enabled" class="icon-checkmark"></p></td>
                            <td>
                                <span v-if="scheduler.all_locations">{{
                                    $t('wanderManagement.schedulersTab.allDoors')
                                }}</span>
                                <span v-if="!scheduler.all_locations && scheduler.door">{{
                                    scheduler.door.name
                                }}</span>
                            </td>
                            <td
                                v-text="
                                    scheduler.door_state
                                        ? $t('wanderManagement.schedulersTab.opened')
                                        : $t('wanderManagement.schedulersTab.closed')
                                "
                            ></td>
                            <td>
                                <span v-if="scheduler.door">
                                    <span
                                        ><img
                                            class="door-icon"
                                            :src="getDoorIcon(scheduler.door.peripheral_icon)"
                                            alt="door"
                                    /></span>
                                    <span>{{ scheduler.door.peripheral_name }}</span>
                                </span>
                                <span v-if="!scheduler.door">{{
                                    $t('wanderManagement.schedulersTab.defaultDoorStatusOutside')
                                }}</span>
                            </td>
                            <td><WeekSchedule v-model="scheduler.time" /></td>
                        </span>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script type="text/babel">
import mappingState from '../common/mappingState';
import SideBar from '../common/Sidebar';
import SchedulersForm from './schedulers/SchedulersForm';
import {
    resourceSchedulers,
    resourceSchedulersWithTime,
    resourceAccessModulesWithLocations,
} from '../../api/resources';
import WeekSchedule from './schedulers/timewindows/WeekSchedule';
import { getCancelToken } from '../../api/index';
import { errorHandler, ifIsNotCanceled } from '../common/helpers';
import { deleteHandler } from '../common/deleteHandler';
import { mapState, mapMutations } from 'vuex';
import Simplebar from 'simplebar-vue';
import { SUCCESS, ERROR, COMPONENT_NAMES, ROUTE_NAMES, USER_TYPES } from '../../constants';

export default {
    name: COMPONENT_NAMES.SCHEDULERS,

    components: {
        SideBar,
        Simplebar,
        SchedulersForm,
        WeekSchedule,
    },

    mixins: [mappingState],

    constants: {
        ROUTE_NAMES: ROUTE_NAMES,
    },

    data() {
        return {
            schedulers: [],
            schedulersCopy: [],
            schedulersForSideBar: [],
            accessModules: [],
            doors: [],
            currentSchedulerId: null,
            currentScheduler: {
                wireless_access_scheduler_id: null,
                name: '',
                enabled: false,
                all_locations: true,
                location_id: null,
                door_state: 0,
                time: null,
            },
            isLoaded: false,
            cancelToken: getCancelToken(),
            showPrintKeyword: false,
            incorrectAA: false,
            showIcons: false,
        };
    },

    computed: {
        ...mapState(['isFormValid', 'unsavedChanges']),

        showSchedulersForm() {
            return (
                this.schedulers.length > 0 ||
                this.$route.name === ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS__ADD
            );
        },

        filteredSchedulersCopy() {
            return this.showPrintKeyword
                ? this.schedulers.filter(item => item.name.toLowerCase().includes(this.searchKeyword))
                : this.schedulers;
        },

        filteredSchedulersCopyWithDoor() {
            return this.filteredSchedulersCopy.map(scheduler => ({
                ...scheduler,
                door: this.getDoorByScheduler(scheduler),
            }));
        },

        forbiddenNames() {
            return this.schedulers.filter(
                s => s.wireless_access_scheduler_id !== this.currentScheduler.wireless_access_scheduler_id
            );
        },

        anotherAllDoorsEnabled() {
            return this.schedulers.some(
                s =>
                    s.all_locations &&
                    s.enabled &&
                    s.wireless_access_scheduler_id !== this.currentScheduler.wireless_access_scheduler_id
            );
        },

        anotherSameDoorsEnabled() {
            return this.schedulers.some(
                s =>
                    !s.all_locations &&
                    s.location_id === this.currentScheduler.location_id &&
                    s.enabled &&
                    s.wireless_access_scheduler_id !== this.currentScheduler.wireless_access_scheduler_id
            );
        },

        restrictedEnableCurrentScheduler() {
            return this.anotherAllDoorsEnabled && !this.currentScheduler.all_locations;
        },

        doorOptions() {
            return this.anotherAllDoorsEnabled && this.currentScheduler.wireless_access_scheduler_id === ''
                ? [{ name: this.$t('wanderManagement.schedulersTab.allDoors'), value: 'null' }]
                : this.doors;
        },
    },

    watch: {
        /**
         * Updates current scheduler's data depending on old and new values.
         * - if all locations is selected -> location id is set to null
         * - if some location is selected -> all_locations is set to false
         * - if there is other enabled 'all doors' scheduler in the list, current scheduler is set to enabled = false
         */
        currentScheduler: {
            handler(newVal, oldVal) {
                if (newVal.location_id !== oldVal.location_id) {
                    if (newVal.location_id === 'null') {
                        this.currentScheduler.all_locations = true;
                        this.currentScheduler.location_id = null;
                    } else if (oldVal.location_id === null && Number.isInteger(newVal.location_id)) {
                        this.currentScheduler.all_locations = false;
                        if (this.restrictedEnableCurrentScheduler) {
                            this.currentScheduler.enabled = false;
                        }
                    }
                }
                this.setUnsavedChanges(true);
            },
            deep: true,
        },

        $route(to) {
            this.setMode(to);
        },

        schedulers: {
            handler(list) {
                let keyword = this.searchKeyword;
                this.showPrintKeyword =
                    keyword && list.some(item => item.name.toLowerCase().includes(keyword));
            },
            deep: true,
        },

        searchKeyword(val) {
            this.showPrintKeyword =
                val && this.schedulers.some(item => item.name.toLowerCase().includes(val));
        },
    },

    created() {
        this.showLoading(true);
        this.updateData(() => {
            this.undo();
            this.showLoading(false);
            return Promise.resolve();
        });
        this.loadAccessModules()
            .then(this.loadSchedulers)
            .then(() => {
                if (this.userType === USER_TYPES.SUPER_USER && this.incorrectAA) {
                    this.popup({
                        type: ERROR,
                        header: this.$t('common.error'),
                        text: this.$t('error.common.forbidden'),
                    });
                    errorHandler(403, ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS);
                } else {
                    this.setMode(this.$route);
                }
                this.isLoaded = true;
                this.showLoading(false);
            })
            .catch(
                ifIsNotCanceled({
                    callback: response =>
                        errorHandler(response.status, ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS),
                })
            );
    },

    beforeDestroy() {
        this.cancelToken.cancel();
        this.setFormValid(true);
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),

        /**
         * Deletes scheduler by id
         * @param id
         * @return {*}
         */
        deleteById(id) {
            let deletedScheduler = this.schedulers.find(item => item.wireless_access_scheduler_id === id);
            let confirmPart = {
                confirmTitle: this.$t('wanderManagement.schedulersTab.removingSchedulerTitle'),
                confirmBody: this.$t('wanderManagement.schedulersTab.removingScheduler', {
                    name: deletedScheduler ? deletedScheduler.name : '',
                }),
            };
            return deleteHandler(
                resourceSchedulers,
                id,
                this.loadSchedulers,
                ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS,
                confirmPart
            );
        },

        /**
         * Prepare scheduler form for adding new scheduler
         */
        initAdding() {
            this.setEmptyScheduler();
            this.setAddingButtons({
                cancel: () => {
                    this.setUnsavedChanges(false);
                    this.$nextTick(() => {
                        this.$router.push({ name: ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS });
                    });
                },
                save: () => {
                    if (this.isFormValid) {
                        this.save();
                    }
                },
                saveDisabled: () => !this.unsavedChanges || !this.isFormValid,
            });
        },

        /**
         * Prepare scheduler form for editing existing scheduler
         * @param id
         */
        initEditing(id) {
            this.setEditingButtons({
                add: () => this.$router.push({ name: ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS__ADD }),
                undo: () => this.undo(),
                wasChanged: () => !this.unsavedChanges,
                save: () => {
                    if (this.isFormValid) {
                        this.save();
                    }
                },
                saveDisabled: () => !this.unsavedChanges || !this.isFormValid,
            });
            this.setCurrentScheduler(id);
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        /**
         * Loads all schedulers for user
         * @return {Promise}
         */
        loadSchedulers() {
            return resourceSchedulersWithTime({
                cancelToken: this.cancelToken.token,
            }).then(response => {
                this.schedulers = response.data ? response.data : [];
                this.schedulersCopy = response.data ? response.data : [];
                this.schedulersForSideBar = this.schedulers.map(s => ({
                    id: s.wireless_access_scheduler_id,
                    name: s.name,
                    enabled: s.enabled,
                }));
                this.showIcons = this.schedulersForSideBar.some(scheduler => {
                    return this.isEnabled(scheduler);
                });
                this.isLoaded = true;
            });
        },

        /**
         * Forms list of unique locations with existing access modules
         * @return {Promise}
         */
        loadAccessModules() {
            this.incorrectAA = false;
            return resourceAccessModulesWithLocations({
                cancelToken: this.cancelToken.token,
            }).then(response => {
                this.accessModules = response.data ? response.data : [];
                if (
                    this.accessModules.length &&
                    this.accessModules[0].assignmentarea_id !== this.userAreaId
                ) {
                    this.incorrectAA = true;
                }
                for (let module of this.accessModules) {
                    if (module.location && this.doors.every(d => d.value !== module.location.location_id)) {
                        this.doors.push({
                            name: module.location.location_name,
                            value: module.location.location_id,
                            peripheral_icon: module.peripheral_icon,
                            peripheral_name: module.peripheral_name,
                        });
                    }
                }
                this.doors.push({ name: this.$t('wanderManagement.schedulersTab.allDoors'), value: 'null' });
            });
        },

        enableSchedulerConfirm() {
            if (
                this.currentScheduler.location_id === null &&
                !!this.currentScheduler.all_locations &&
                this.currentScheduler.enabled &&
                this.schedulers.some(s => s.enabled)
            ) {
                return this.confirmation({
                    confirmTitle: this.$t('common.confirmTitle'),
                    confirmBody: this.$t('wanderManagement.schedulersTab.confirmationBody'),
                });
            } else if (
                this.currentScheduler.location_id !== null &&
                !this.currentScheduler.all_locations &&
                this.currentScheduler.enabled &&
                this.anotherSameDoorsEnabled
            ) {
                return this.confirmation({
                    confirmTitle: this.$t('common.confirmTitle'),
                    confirmBody: this.$t('wanderManagement.schedulersTab.confirmationSingleBody'),
                });
            }

            return Promise.resolve(true);
        },

        /**
         * Redirects to new scheduler from data
         */
        redirectToNewAdded(data) {
            if (!this.currentScheduler.wireless_access_scheduler_id) {
                if (data && data.wireless_access_scheduler_id) {
                    this.$router.replace({
                        name: ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS__EDIT,
                        params: {
                            id: data.wireless_access_scheduler_id,
                        },
                    });
                }
            }
        },

        /**
         * Saves current scheduler
         * @return {Promise}
         */
        save() {
            this.showLoading(true);

            return this.enableSchedulerConfirm()
                .then(r => {
                    if (r) {
                        let time =
                            this.currentScheduler.time !== null
                                ? this.currentScheduler.time.filter(
                                      t => t.begin_time !== undefined && t.end_time !== undefined
                                  )
                                : null;

                        this.currentScheduler.time = time;
                        return resourceSchedulers({
                            method: 'post',
                            id: this.currentScheduler.wireless_access_scheduler_id
                                ? this.currentScheduler.wireless_access_scheduler_id
                                : null,
                            data: JSON.stringify(this.currentScheduler),
                        })
                            .then(response => {
                                let jsonData = response.data;
                                this.popup({
                                    type: SUCCESS,
                                    header: this.$t('common.success'),
                                    text: this.$t('common.saved'),
                                });
                                return this.loadSchedulers().then(() => {
                                    this.showLoading(false);
                                    this.setUnsavedChanges(false);
                                    // redirect to newly added
                                    this.$nextTick(() => {
                                        this.redirectToNewAdded(jsonData);
                                    });
                                });
                            })
                            .catch(
                                ifIsNotCanceled({
                                    callback: response => {
                                        if (response.status === 404 || response.status === 409) {
                                            return this.loadSchedulers().then(() => {
                                                this.showLoading(false);
                                                this.setUnsavedChanges(false);
                                                this.$nextTick(() => {
                                                    this.$router.replace({
                                                        name: ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS,
                                                    });
                                                });
                                            });
                                        }
                                        return Promise.resolve();
                                    },
                                })
                            );
                    }
                    this.showLoading(false);
                })
                .catch(() => this.showLoading(false));
        },

        setCurrentScheduler(id) {
            let scheduler = this.schedulers.find(i => i.wireless_access_scheduler_id === id);
            if (scheduler) {
                this.currentScheduler = { ...scheduler };
            } else {
                this.popup({
                    type: ERROR,
                    header: this.$t('common.error'),
                    text: this.$t('wanderManagement.schedulersTab.notFound'),
                });
                errorHandler(404, ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS);
            }
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        setEmptyScheduler() {
            this.currentScheduler = {
                wireless_access_scheduler_id: '',
                name: '',
                enabled: false,
                all_locations: true,
                location_id: null,
                door_state: 0,
                time: null,
            };
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        /**
         * Set property for visibility by route's name and redirect to first item if it needs
         * @param route
         */
        setMode(route) {
            if (route.name === ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS) {
                if (this.schedulers.length) {
                    let firstId = this.schedulers[0].wireless_access_scheduler_id;
                    this.$router.replace({
                        name: ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS__EDIT,
                        params: { id: firstId },
                    });
                    this.initEditing(firstId);
                } else {
                    this.configButton({
                        buttonName: 'addButton',
                        action: () =>
                            this.$router.push({ name: ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS__ADD }),
                        disabled: () => this.accessModules.length === 0,
                    });
                }
            } else if (route.name === ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS__ADD) {
                this.currentSchedulerId = null;
                this.initAdding();
            } else {
                if (
                    this.schedulers.length &&
                    this.schedulers.some(s => s.wireless_access_scheduler_id === Number(route.params.id))
                ) {
                    this.currentSchedulerId = Number(route.params.id);
                    this.initEditing(this.currentSchedulerId);
                } else {
                    this.$router.replace({
                        name: ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS,
                    });
                }
            }
        },

        undo() {
            let id = this.currentSchedulerId;
            if (id) {
                this.setCurrentScheduler(id);
            } else {
                this.$router.push({ name: ROUTE_NAMES.WANDER_MANAGEMENT__SCHEDULERS });
            }
        },

        isEnabled(scheduler) {
            return !!scheduler.enabled;
        },

        getDoorByScheduler(scheduler) {
            return this.doors.find(door => door.value === scheduler.location_id);
        },

        getDoorIcon(iconName) {
            return `static/img/devices/${iconName}_24px.png`;
        },
    },
};
</script>

<style scoped></style>
